import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { handleApiError } from '../../utils/api/errorHandling';
import { handleNetworkError, isNetworkError } from '../../utils/api/networkErrors';
import { mockJobs } from '../../data/mockJobs';
import type { Job } from '../../types/job';

export async function getJobs() {
  try {
    const { data, error } = await supabase
      .from('jobs')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Database error:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching jobs:', error);
    if (isNetworkError(error)) {
      toast.error('Connection error. Using offline data.', {
        id: 'offline-jobs'
      });
      return mockJobs;
    }
    throw error;
  }
}

async function getJobById(jobId: string) {
  try {
    const { data, error } = await supabase
      .from('jobs')
      .select('*')
      .eq('id', jobId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching job:', error);
    throw error;
  }
}

export async function createJob(jobData: Omit<Job, 'id'>) {
  try {
    // Check admin status first
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Authentication required');

    const { data: profile, error: profileError } = await supabase
      .rpc('verify_admin_for_jobs');
    
    if (profileError || !profile) throw new Error('Admin access required');

    const { data, error } = await supabase
      .from('jobs')
      .insert({
        ...jobData,
        status: 'active',
        posted_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('Failed to create job');

    toast.success('Job created successfully');
    return data;
  } catch (error) {
    console.error('Error creating job:', error);
    if (isNetworkError(error)) {
      handleNetworkError(error);
    } else {
      toast.error(error instanceof Error ? error.message : 'Failed to create job');
    }
    throw new Error('Failed to create job');
  }
}

export async function updateJob(jobId: string, jobData: Partial<Job>) {
  try {
    // Map frontend model to database columns
    const dbJob = {
      ...(jobData.title && { title: jobData.title }),
      ...(jobData.company && { company: jobData.company }),
      ...(jobData.location && { location: jobData.location }),
      ...(jobData.type && { type: jobData.type }),
      ...(jobData.salary && { salary: jobData.salary }),
      ...(jobData.logo && { logo: jobData.logo }),
      ...(jobData.description && { description: jobData.description }),
      ...(jobData.technicalSkills && { technical_skills: jobData.technicalSkills }),
      ...(jobData.softSkills && { soft_skills: jobData.softSkills }),
      ...(jobData.requirements && { requirements: jobData.requirements }),
      ...(jobData.responsibilities && { responsibilities: jobData.responsibilities }),
      ...(jobData.benefits && { benefits: jobData.benefits }),
      updated_at: new Date().toISOString()
    };

    const { error: updateError } = await supabase
      .from('jobs')
      .update({
        ...dbJob,
        updated_at: new Date().toISOString()
      })
      .eq('id', jobId);

    if (updateError) throw updateError;

    // Fetch the updated job
    const { data: updatedJob, error: fetchError } = await supabase
      .from('jobs')
      .select('*')
      .eq('id', jobId)
      .single();

    if (fetchError) throw fetchError;
    toast.success('Job updated successfully');
    return updatedJob;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export async function deleteJob(jobId: string) {
  try {
    const { error } = await supabase
      .from('jobs')
      .delete()
      .eq('id', jobId);

    if (error) throw error;
    toast.success('Job deleted successfully');
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}